import { useEffect, useState } from "react";
import { IconFileText, IconLoader2 } from "@tabler/icons-react";
import { PayableInvoice, supplierInvoiceFileUrl } from "../../api/queries";
import { PaymentStatusBadge } from "./PaymentStatusBadge";

interface InvoiceForPaymentRowProps {
  invoice: PayableInvoice;
  canManage: boolean;
  isBusy: boolean;
  onFieldChange: (fields: {
    vendor_name?: string;
    description?: string;
    notes?: string;
    invoice_date?: string;
    amount?: number;
  }) => void;
  onToggleApproved: (approved: boolean) => void;
  onTogglePaid: (paid: boolean, paymentDate?: string) => void;
}

/** Date inputs want YYYY-MM-DD; the API sends full ISO timestamps. */
const toDateInputValue = (iso: string | null): string =>
  iso ? iso.split("T")[0] : "";

const todayInputValue = (): string => new Date().toISOString().split("T")[0];

export const InvoiceForPaymentRow = ({
  invoice,
  canManage,
  isBusy,
  onFieldChange,
  onToggleApproved,
  onTogglePaid,
}: InvoiceForPaymentRowProps) => {
  const [vendor, setVendor] = useState(invoice.vendor_name);
  const [description, setDescription] = useState(invoice.description ?? "");
  const [notes, setNotes] = useState(invoice.notes ?? "");
  const [invoiceDate, setInvoiceDate] = useState(
    toDateInputValue(invoice.invoice_date),
  );
  const [amount, setAmount] = useState(
    invoice.amount === null ? "" : String(invoice.amount),
  );
  const [paymentDate, setPaymentDate] = useState(
    toDateInputValue(invoice.paid_at),
  );

  // Re-sync when the server sends new values (e.g. after extraction fills a
  // blank amount) so the inputs never show stale text.
  useEffect(() => {
    setVendor(invoice.vendor_name);
    setDescription(invoice.description ?? "");
    setNotes(invoice.notes ?? "");
    setInvoiceDate(toDateInputValue(invoice.invoice_date));
    setAmount(invoice.amount === null ? "" : String(invoice.amount));
    setPaymentDate(toDateInputValue(invoice.paid_at));
  }, [
    invoice.vendor_name,
    invoice.description,
    invoice.notes,
    invoice.invoice_date,
    invoice.amount,
    invoice.paid_at,
  ]);

  const isApproved = invoice.status !== "pending_approval";
  const isPaid = invoice.status === "paid";
  const isAwaitingExtraction = invoice.extraction_status === "pending";

  const commitVendor = () => {
    const next = vendor.trim();
    if (next && next !== invoice.vendor_name) {
      onFieldChange({ vendor_name: next });
    }
  };

  const commitDescription = () => {
    const next = description.trim();
    if (next !== (invoice.description ?? "")) {
      onFieldChange({ description: next });
    }
  };

  const commitNotes = () => {
    const next = notes.trim();
    if (next !== (invoice.notes ?? "")) {
      onFieldChange({ notes: next });
    }
  };

  const commitInvoiceDate = () => {
    if (invoiceDate && invoiceDate !== toDateInputValue(invoice.invoice_date)) {
      onFieldChange({ invoice_date: `${invoiceDate}T00:00:00` });
    }
  };

  const commitAmount = () => {
    const parsed = Number(amount);
    if (amount.trim() === "" || Number.isNaN(parsed)) return;
    if (parsed !== invoice.amount) {
      onFieldChange({ amount: parsed });
    }
  };

  /** On a paid row, changing the date re-records the payment on that date. */
  const commitPaymentDate = () => {
    if (!isPaid || !paymentDate) return;
    if (paymentDate !== toDateInputValue(invoice.paid_at)) {
      onTogglePaid(true, `${paymentDate}T00:00:00`);
    }
  };

  const cellInput =
    "w-full bg-transparent border border-transparent rounded px-2 py-1 hover:border-gray-300 focus:border-slate-500 focus:bg-white focus:ring-1 focus:ring-slate-500 outline-none transition disabled:hover:border-transparent";

  return (
    <tr className="border-b border-gray-100 hover:bg-slate-50/60">
      <td className="px-3 py-2 align-middle">
        <input
          aria-label="Vendor name"
          value={vendor}
          disabled={isPaid}
          onChange={(e) => setVendor(e.target.value)}
          onBlur={commitVendor}
          className={`${cellInput} font-medium text-gray-800 min-w-[10rem]`}
        />
      </td>

      <td className="px-3 py-2 align-middle">
        <input
          aria-label="Description"
          value={description}
          disabled={isPaid}
          placeholder={isAwaitingExtraction ? "Reading PDF…" : "—"}
          onChange={(e) => setDescription(e.target.value)}
          onBlur={commitDescription}
          className={`${cellInput} text-gray-600 min-w-[10rem]`}
        />
      </td>

      <td className="px-3 py-2 align-middle">
        <input
          aria-label="Notes"
          value={notes}
          placeholder="—"
          onChange={(e) => setNotes(e.target.value)}
          onBlur={commitNotes}
          className={`${cellInput} text-gray-600 min-w-[10rem]`}
        />
      </td>

      <td className="px-3 py-2 align-middle whitespace-nowrap">
        <input
          type="date"
          aria-label="Invoice date"
          value={invoiceDate}
          disabled={isPaid}
          onChange={(e) => setInvoiceDate(e.target.value)}
          onBlur={commitInvoiceDate}
          className={`${cellInput} text-gray-600`}
        />
      </td>

      <td className="px-3 py-2 align-middle whitespace-nowrap text-right">
        <div className="flex items-center justify-end gap-1">
          <input
            type="number"
            step="0.01"
            aria-label="Amount"
            value={amount}
            disabled={isPaid}
            placeholder={isAwaitingExtraction ? "…" : "—"}
            onChange={(e) => setAmount(e.target.value)}
            onBlur={commitAmount}
            className={`${cellInput} text-right font-semibold text-gray-800 w-28`}
          />
          <span className="text-xs text-gray-400 w-8 shrink-0">
            {invoice.currency ?? ""}
          </span>
        </div>
      </td>

      <td className="px-3 py-2 align-middle text-center">
        <input
          type="checkbox"
          aria-label="Approved for payment"
          checked={isApproved}
          disabled={!canManage || isBusy || isPaid}
          onChange={(e) => onToggleApproved(e.target.checked)}
          className="h-4 w-4 rounded border-gray-300 text-slate-600 focus:ring-slate-500 disabled:opacity-40 disabled:cursor-not-allowed"
        />
      </td>

      <td className="px-3 py-2 align-middle">
        <PaymentStatusBadge status={invoice.status} />
      </td>

      <td className="px-3 py-2 align-middle whitespace-nowrap">
        <input
          type="date"
          aria-label="Payment date"
          value={paymentDate}
          disabled={!canManage}
          title={
            isPaid
              ? "Change the date this invoice was paid"
              : "Date to record when marking this invoice paid"
          }
          onChange={(e) => setPaymentDate(e.target.value)}
          onBlur={commitPaymentDate}
          className={`${cellInput} text-gray-600`}
        />
      </td>

      <td className="px-3 py-2 align-middle whitespace-nowrap">
        <div className="flex items-center gap-2 justify-end">
          {invoice.is_demo ? (
            <span
              title="Demo invoice — no file attached"
              className="text-gray-300 p-1 cursor-not-allowed"
            >
              <IconFileText size={18} />
            </span>
          ) : (
            <a
              href={supplierInvoiceFileUrl(invoice)}
              target="_blank"
              rel="noreferrer"
              title={invoice.filename}
              className="text-emerald-600 hover:text-emerald-700 p-1"
            >
              <IconFileText size={18} />
            </a>
          )}

          {isBusy ? (
            <IconLoader2 size={18} className="animate-spin text-gray-400" />
          ) : (
            <button
              type="button"
              disabled={!canManage}
              onClick={() =>
                onTogglePaid(
                  !isPaid,
                  isPaid
                    ? undefined
                    : `${paymentDate || todayInputValue()}T00:00:00`,
                )
              }
              className={
                isPaid
                  ? "px-3 py-1 text-xs font-medium rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  : "px-3 py-1 text-xs font-medium rounded-lg bg-slate-700 text-white hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed"
              }
            >
              {isPaid ? "Undo paid" : "Mark paid"}
            </button>
          )}
        </div>
      </td>
    </tr>
  );
};
